/**
 * WHICH DEBTS THE ENGINES TEST CLEARING — one rule for all six modules (owner, 26 Oct 2026).
 *
 * Until then every module tested clearing the debts only once they added up to $5,000, on the reasoning
 * that a smaller payoff moves the Age Pension by too little to change a ranking (the assets-test taper on
 * $5,000 is about $390 a year). That counted the pension and forgot the interest: a $4,000 card at 21%
 * costs about $840 a year, more than twice that, and clearing it from a bank account earning a few per
 * cent almost always wins — yet no card ever said so.
 *
 * So, now:
 *  - A credit card paid off in full every month is NOT a debt to decide about. It costs no interest and
 *    next month brings a new balance paid from the same account; "clearing" it would only make the cash
 *    Centrelink counts look permanently smaller, a pension gain that never happens. It stays counted as
 *    owed (a month's bill) in today's figures and the projection (creditCards.ts), but no plan clears it.
 *  - A debt that costs interest (a card on the minimum, a set amount or "not sure"; a personal loan; a
 *    mortgage with a rate) is ALWAYS tested, whatever its size.
 *  - A debt that costs no interest (0%, interest-free) is tested only when such debts reach
 *    ZERO_INTEREST_DEBT_FLOOR together, because the Age Pension is then the only gain.
 *
 * The engines keep their existing twins (every plan, also with these debts cleared), so a client with a
 * small interest-bearing card costs the same to analyse as one with a large debt already did.
 */
export const ZERO_INTEREST_DEBT_FLOOR = 5_000;

export interface TestableDebt {
    balance: number;
    interestRate: number;
    /** A credit card paid off in full each month (creditCards.ts cardPaymentStyle 'full'). */
    paidInFull?: boolean;
}

/** The debts a plan may clear: never a card paid in full; interest-bearing always; interest-free from the floor. */
export function debtsWorthTesting<T extends TestableDebt>(debts: readonly T[]): T[] {
    const decidable = debts.filter((d) => !d.paidInFull && d.balance > 0);
    const interestFree = decidable.filter((d) => !(d.interestRate > 0));
    const interestFreeTotal = interestFree.reduce((sum, d) => sum + d.balance, 0);
    return interestFreeTotal >= ZERO_INTEREST_DEBT_FLOOR ? decidable : decidable.filter((d) => d.interestRate > 0);
}

/** Cards paid off in full each month, for the one line that says why they are not part of the plan. */
export function paidInFullCards<T extends TestableDebt & { description?: string }>(debts: readonly T[]): T[] {
    return debts.filter((d) => d.paidInFull && d.balance > 0);
}

/** "The CBA card is paid off in full each month, so it costs no interest and isn't part of the plan." */
export function paidInFullLine(debts: readonly (TestableDebt & { description?: string })[]): string | null {
    const cards = paidInFullCards(debts);
    if (cards.length === 0) return null;
    const names = cards.map((c) => String(c.description || 'credit card').trim() || 'credit card');
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    return cards.length === 1
        ? `The ${list} is paid off in full each month, so it costs no interest and isn't part of the plan.`
        : `The ${list} are paid off in full each month, so they cost no interest and aren't part of the plan.`;
}
